'use client';

import { useEffect, useState } from 'react';
import { Cpu, ArrowRight, X } from 'lucide-react';
import {
  buildWideAreaAIUrl,
  wideAreaAICtaName,
  type WideAreaAIVariant,
} from '@/components/WideAreaAICTA';
import { trackCtaClick } from '@/lib/analytics';

/**
 * Sticky/floating Wide Area Intelligence CTA for KB funnel pages.
 *
 * KB traffic on inventivehq is ~92% desktop, and the existing CTAs only live in
 * the TOC sidebar (peripheral) and after the full article (terminal) — neither
 * is in front of a reader mid-article, which is why the funnel CTR was ~0. This
 * pins a compact card bottom-right once the reader scrolls into the body, so the
 * offer is visible the whole way down without interrupting the read.
 *
 * - placement = "sticky" → folded into utm_content + cta_name so we can compare
 *   this slot's CTR against sidebar / inline-end in GA4.
 * - Dismissible; the dismissal sticks for the session (sessionStorage) so we
 *   never nag a reader who said no.
 * - Plain <a> + tracked onClick, mirroring WideAreaAICTA.
 */

const PLACEMENT = 'sticky';
const DISMISS_KEY = 'wai_sticky_dismissed';
/** px scrolled before the card appears — past the hero, into the article body. */
const SHOW_AFTER = 600;

const HOOK: Record<WideAreaAIVariant, string> = {
  'local-models': 'Turn the GPU you own into an OpenAI-compatible endpoint — free.',
  'rate-limits': 'Your own GPU never rate-limits you.',
  'token-costs': 'Stop paying per token — route to a GPU you own.',
};

interface Props {
  variant: WideAreaAIVariant;
  /** Source page slug, for utm_content + click tracking. */
  source: string;
}

export default function WideAreaAIStickyCTA({ variant, source }: Props) {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.sessionStorage.getItem(DISMISS_KEY) === '1') {
      setDismissed(true);
      return;
    }
    const onScroll = () => {
      if (window.scrollY > SHOW_AFTER) {
        setShow(true);
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // in case the page is already scrolled (back-nav, anchor link)
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (dismissed || !show) return null;

  const href = buildWideAreaAIUrl(variant, source, 'kb_cta', PLACEMENT);

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* sessionStorage may be unavailable (private mode); dismissal still works for this view */
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[20rem] max-w-[calc(100vw-2rem)] rounded-lg border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-gray-800 shadow-xl shadow-indigo-900/10">
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="absolute top-2 right-2 rounded-md p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0">
            <Cpu className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">
            Wide Area Intelligence
          </span>
        </div>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 pr-4">{HOOK[variant]}</p>
        <a
          href={href}
          target="_blank"
          rel="nofollow noopener noreferrer"
          onClick={() => trackCtaClick(wideAreaAICtaName(variant, PLACEMENT), 'kb_cta', href)}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white px-4 py-2 rounded-md font-medium text-sm hover:shadow-lg transition-all"
        >
          Start routing — free
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}
